//#region src/emotion.ts
const defaultCompanionMoodPolicy = {
	valenceHalfLifeMs: 360 * 60 * 1e3,
	arousalHalfLifeMs: 5400 * 1e3,
	baselineArousal: .25
};
function clamp$1(value, minimum, maximum) {
	return Math.min(maximum, Math.max(minimum, value));
}
function decayToward(value, baseline, elapsedMs, halfLifeMs) {
	if (elapsedMs <= 0) return value;
	const retention = 2 ** (-elapsedMs / Math.max(1, halfLifeMs));
	return baseline + (value - baseline) * retention;
}
/** Creates neutral persistent mood without scheduling background timers. */
function createCompanionMoodState(now = Date.now()) {
	return {
		valence: 0,
		arousal: defaultCompanionMoodPolicy.baselineArousal,
		updatedAt: now
	};
}
/** Derives a stable presentation label from resolved affect dimensions. */
function companionMoodLabel(mood) {
	if (mood.valence >= .35) return "happy";
	if (mood.valence <= -.35) return mood.arousal >= .55 ? "tense" : "sad";
	if (mood.arousal >= .55 && mood.valence >= -.1) return "curious";
	if (mood.arousal <= .15) return "calm";
	return "neutral";
}
/** Resolves lazy exponential decay without mutating or persisting the source state. */
function resolveCompanionMood(mood, now = Date.now(), policy = defaultCompanionMoodPolicy) {
	const resolvedAt = Math.max(mood.updatedAt, now);
	const elapsedMs = Math.max(0, now - mood.updatedAt);
	const resolved = {
		valence: clamp$1(decayToward(mood.valence, 0, elapsedMs, policy.valenceHalfLifeMs), -1, 1),
		arousal: clamp$1(decayToward(mood.arousal, policy.baselineArousal, elapsedMs, policy.arousalHalfLifeMs), 0, 1),
		updatedAt: resolvedAt
	};
	return {
		...resolved,
		label: companionMoodLabel(resolved),
		resolvedAt
	};
}
/** Applies one explicit durable event after first resolving elapsed-time decay. */
function applyCompanionMoodEvent(mood, event, policy = defaultCompanionMoodPolicy) {
	const current = resolveCompanionMood(mood, event.occurredAt, policy);
	let delta;
	if (event.type === "interaction-completed") delta = {
		valence: .02,
		arousal: .08
	};
	else if (event.type === "important-memory-marked") delta = {
		valence: .08,
		arousal: .04
	};
	else if (event.sentiment === "positive") delta = {
		valence: .2,
		arousal: .1
	};
	else delta = {
		valence: -.2,
		arousal: .15
	};
	return {
		valence: clamp$1(current.valence + delta.valence, -1, 1),
		arousal: clamp$1(current.arousal + delta.arousal, 0, 1),
		updatedAt: current.resolvedAt
	};
}
//#endregion
//#region src/life.ts
/** Default policy keeps autonomous behavior infrequent and easy to interrupt. */
const defaultCompanionLifePolicy = {
	idleAfterMs: 1800 * 1e3,
	restAfterMs: 7200 * 1e3,
	behaviorCooldownMs: 1800 * 1e3,
	morningStartsAtHour: 7,
	morningEndsAtHour: 11
};
function normalizedPolicy(policy) {
	const idleAfterMs = Math.max(6e4, policy?.idleAfterMs ?? defaultCompanionLifePolicy.idleAfterMs);
	return {
		idleAfterMs,
		restAfterMs: Math.max(idleAfterMs, policy?.restAfterMs ?? defaultCompanionLifePolicy.restAfterMs),
		behaviorCooldownMs: Math.max(6e4, policy?.behaviorCooldownMs ?? defaultCompanionLifePolicy.behaviorCooldownMs),
		morningStartsAtHour: Math.min(23, Math.max(0, Math.floor(policy?.morningStartsAtHour ?? defaultCompanionLifePolicy.morningStartsAtHour))),
		morningEndsAtHour: Math.min(24, Math.max(1, Math.floor(policy?.morningEndsAtHour ?? defaultCompanionLifePolicy.morningEndsAtHour)))
	};
}
function localDateKey(timestamp) {
	const date = new Date(timestamp);
	return [
		date.getFullYear(),
		date.getMonth() + 1,
		date.getDate()
	].map((value) => value.toString().padStart(2, "0")).join("-");
}
function normalizedCueValue(value) {
	return Array.from(value.replace(/\s+/g, " ").trim()).slice(0, 120).join("");
}
/**
* Selects one deterministic, locally dated personalization cue.
*
* Only explicit profile interests and tentative reflection observations are
* eligible. Resting stays generic so extended inactivity is never framed as
* evidence about the user.
*/
function resolveCompanionLifeMessageCue(kind, context, now) {
	if (kind === "resting") return void 0;
	const cues = [...context.interests.map((value) => ({
		type: "interest",
		value: normalizedCueValue(value)
	})), ...context.learned.map((value) => ({
		type: "tentative-observation",
		value: normalizedCueValue(value)
	}))].filter((cue) => cue.value.length > 0);
	if (!cues.length) return void 0;
	const date = new Date(now);
	return cues[Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 864e5) % cues.length];
}
function idleBehavior(input, policy) {
	const idleForMs = Math.max(0, input.now - input.lastActiveAt);
	if (idleForMs >= policy.restAfterMs) return "resting";
	if (idleForMs < policy.idleAfterMs) return void 0;
	if (input.personality.creativity > input.personality.curiosity) return "idle-creative";
	if (input.personality.curiosity > input.personality.creativity) return "idle-curious";
	return Math.floor(input.now / policy.behaviorCooldownMs) % 2 === 0 ? "idle-curious" : "idle-creative";
}
/**
* Resolves one autonomous desktop-life behavior.
*
* Busy, hidden, or disabled surfaces never emit a decision. Morning greetings
* are limited to once per local day, while idle behaviors share one cooldown.
*/
function resolveCompanionLifeBehavior(input) {
	if (!input.enabled || input.busy || !input.visible) return void 0;
	const policy = normalizedPolicy(input.policy);
	const currentDate = localDateKey(input.now);
	const hour = new Date(input.now).getHours();
	const idleForMs = Math.max(0, input.now - input.lastActiveAt);
	const inMorningWindow = hour >= policy.morningStartsAtHour && hour < policy.morningEndsAtHour;
	if (input.morningGreetingEnabled && inMorningWindow && idleForMs < policy.idleAfterMs && input.previous.lastMorningGreetingDate !== currentDate) return {
		kind: "morning-greeting",
		occurredAt: input.now,
		nextState: {
			...input.previous,
			lastBehaviorAt: input.now,
			lastMorningGreetingDate: currentDate
		}
	};
	const lastBehaviorAt = input.previous.lastBehaviorAt ?? 0;
	if (input.now - lastBehaviorAt < policy.behaviorCooldownMs) return void 0;
	const kind = idleBehavior(input, policy);
	if (!kind) return void 0;
	return {
		kind,
		occurredAt: input.now,
		nextState: {
			...input.previous,
			lastBehaviorAt: input.now
		}
	};
}
//#endregion
//#region src/profile.ts
const MAX_PROFILE_ITEMS = 20;
const MAX_PROFILE_ITEM_LENGTH = 100;
/**
* Normalizes one identity-profile item before comparison or persistence.
*
* Before:
* - `"  watercolor painting  "`
*
* After:
* - `"watercolor painting"`
*/
function normalizeCompanionIdentityProfileItem(item) {
	return item.trim().slice(0, MAX_PROFILE_ITEM_LENGTH);
}
/** Returns whether an observation has already been explicitly confirmed in one identity field. */
function isCompanionIdentityObservationConfirmed(profile, kind, observation) {
	const normalized = normalizeCompanionIdentityProfileItem(observation);
	if (!normalized) return false;
	return kind === "interest" ? profile.interests.includes(normalized) : profile.values.includes(normalized);
}
/**
* Builds the smallest identity update for an explicitly confirmed observation.
* The tentative reflection entry remains unchanged as provenance.
*/
function createCompanionIdentityPromotionUpdate(profile, kind, observation) {
	const normalized = normalizeCompanionIdentityProfileItem(observation);
	if (!normalized || isCompanionIdentityObservationConfirmed(profile, kind, normalized)) return void 0;
	return kind === "interest" ? { interests: [...profile.interests, normalized] } : { values: [...profile.values, normalized] };
}
/**
* Normalizes identity-profile list items.
*
* Before:
* - `[" drawing ", "", "drawing"]`
*
* After:
* - `["drawing"]`
*/
function normalizeProfileItems(items) {
	return Array.from(new Set(items.map(normalizeCompanionIdentityProfileItem).filter(Boolean))).slice(0, MAX_PROFILE_ITEMS);
}
function normalizeBirthday(birthday) {
	const date = new Date(birthday);
	if (!Number.isFinite(date.getTime())) throw new Error("Companion identity profiles require a valid birthday.");
	return date.toISOString();
}
/** Creates an empty identity profile for one persisted companion relationship. */
function createCompanionIdentityProfile(scope, birthday, now = Date.now()) {
	return {
		schemaVersion: 1,
		scope: { ...scope },
		birthday: normalizeBirthday(birthday),
		interests: [],
		values: [],
		updatedAt: now
	};
}
/** Updates editable profile fields while preserving relationship ownership and birthday. */
function updateCompanionIdentityProfile(profile, update, now = Date.now()) {
	return {
		...profile,
		interests: update.interests === void 0 ? profile.interests : normalizeProfileItems(update.interests),
		values: update.values === void 0 ? profile.values : normalizeProfileItems(update.values),
		updatedAt: now
	};
}
//#endregion
//#region src/world.ts
/** Explicitly suppresses the character-authored room fallback for one Personal World scope. */
const PERSONAL_WORLD_NO_ACTIVE_ROOM_ID = "none";
/** Maximum existing creations one project can reference without copying asset data. */
const PERSONAL_WORLD_PROJECT_CREATION_LIMIT = 24;
function normalizedCreationIds(creationIds) {
	const normalized = Array.from(new Set((creationIds ?? []).map((id) => id.trim()).filter(Boolean)));
	if (normalized.length > 24) throw new Error(`Personal World projects support at most 24 creations.`);
	return normalized;
}
/** Creates a normalized Personal World entry and rejects empty durable content. */
function createPersonalWorldEntry(input) {
	const id = input.id.trim();
	const title = input.title.trim();
	const content = input.content.trim();
	if (!id) throw new Error("Personal World entries require an id.");
	if (!content) throw new Error("Personal World entries require content.");
	const now = input.now ?? Date.now();
	return {
		schemaVersion: 1,
		id,
		scope: { ...input.scope },
		kind: input.kind,
		title,
		content,
		source: { ...input.source },
		createdAt: now,
		updatedAt: now
	};
}
/** Creates a validated creative project without taking ownership of linked assets. */
function createPersonalWorldProject(input) {
	const id = input.id.trim();
	const title = input.title.trim();
	const description = input.description.trim();
	if (!id) throw new Error("Personal World projects require an id.");
	if (!title) throw new Error("Personal World projects require a title.");
	if (!description) throw new Error("Personal World projects require a description.");
	const now = input.now ?? Date.now();
	const status = input.status ?? "idea";
	return {
		schemaVersion: 1,
		id,
		scope: { ...input.scope },
		title,
		description,
		status,
		creationIds: normalizedCreationIds(input.creationIds),
		createdAt: now,
		updatedAt: now,
		...status === "completed" ? { completedAt: now } : {}
	};
}
/** Updates project content and keeps completion time aligned with its lifecycle state. */
function updatePersonalWorldProject(project, update, now = Date.now()) {
	const title = update.title?.trim() ?? project.title;
	const description = update.description?.trim() ?? project.description;
	if (!title) throw new Error("Personal World projects require a title.");
	if (!description) throw new Error("Personal World projects require a description.");
	const status = update.status ?? project.status;
	const creationIds = update.creationIds === void 0 ? [...project.creationIds] : normalizedCreationIds(update.creationIds);
	if (title === project.title && description === project.description && status === project.status && creationIds.length === project.creationIds.length && creationIds.every((id, index) => id === project.creationIds[index])) return project;
	return {
		...project,
		title,
		description,
		status,
		creationIds,
		updatedAt: now,
		...status === "completed" ? { completedAt: project.completedAt ?? now } : { completedAt: void 0 }
	};
}
//#endregion
//#region src/index.ts
/** Default policy for gradual, interaction-driven companion development. */
const defaultCompanionDevelopmentPolicy = {
	childAtGrowthPoints: 5,
	companionAtGrowthPoints: 30,
	independentAtGrowthPoints: 100,
	reflectionEveryInteractions: 10,
	maxReflections: 12
};
const neutralPersonality = {
	curiosity: .5,
	creativity: .5,
	kindness: .5,
	humor: .5
};
function clamp(value, minimum, maximum) {
	return Math.min(maximum, Math.max(minimum, value));
}
function normalizeThreshold(value, fallback) {
	return Number.isFinite(value) ? Math.max(1, Math.floor(value)) : fallback;
}
function normalizePolicy(policy) {
	const childAtGrowthPoints = normalizeThreshold(policy.childAtGrowthPoints, 5);
	const companionAtGrowthPoints = Math.max(childAtGrowthPoints, normalizeThreshold(policy.companionAtGrowthPoints, 30));
	return {
		childAtGrowthPoints,
		companionAtGrowthPoints,
		independentAtGrowthPoints: Math.max(companionAtGrowthPoints, normalizeThreshold(policy.independentAtGrowthPoints, 100)),
		reflectionEveryInteractions: normalizeThreshold(policy.reflectionEveryInteractions, 10),
		maxReflections: normalizeThreshold(policy.maxReflections, 12)
	};
}
function growthStageFor(growthPoints, policy) {
	if (growthPoints >= policy.independentAtGrowthPoints) return "independent";
	if (growthPoints >= policy.companionAtGrowthPoints) return "companion";
	if (growthPoints >= policy.childAtGrowthPoints) return "child";
	return "seed";
}
/**
* Resolves growth progress without exposing stage thresholds to presentation layers.
* Independent companions have no next stage and report complete progress.
*/
function getCompanionDevelopmentProgress(growthPoints, policy = defaultCompanionDevelopmentPolicy) {
	const normalized = normalizePolicy(policy);
	const completedGrowthPoints = Math.max(0, Math.floor(growthPoints));
	const currentStage = growthStageFor(completedGrowthPoints, normalized);
	const boundaries = {
		seed: {
			currentStageStartedAtGrowthPoints: 0,
			nextStage: "child",
			nextStageAtGrowthPoints: normalized.childAtGrowthPoints
		},
		child: {
			currentStageStartedAtGrowthPoints: normalized.childAtGrowthPoints,
			nextStage: "companion",
			nextStageAtGrowthPoints: normalized.companionAtGrowthPoints
		},
		companion: {
			currentStageStartedAtGrowthPoints: normalized.companionAtGrowthPoints,
			nextStage: "independent",
			nextStageAtGrowthPoints: normalized.independentAtGrowthPoints
		}
	};
	if (currentStage === "independent") return {
		currentStage,
		currentStageStartedAtGrowthPoints: normalized.independentAtGrowthPoints,
		remainingGrowthPoints: 0,
		progress: 1
	};
	const boundary = boundaries[currentStage];
	const growthPointsInStage = completedGrowthPoints - boundary.currentStageStartedAtGrowthPoints;
	const growthPointsForStage = boundary.nextStageAtGrowthPoints - boundary.currentStageStartedAtGrowthPoints;
	return {
		currentStage,
		nextStage: boundary.nextStage,
		currentStageStartedAtGrowthPoints: boundary.currentStageStartedAtGrowthPoints,
		nextStageAtGrowthPoints: boundary.nextStageAtGrowthPoints,
		remainingGrowthPoints: Math.max(0, boundary.nextStageAtGrowthPoints - completedGrowthPoints),
		progress: clamp(growthPointsInStage / growthPointsForStage, 0, 1)
	};
}
function normalizedLearned(learned) {
	return Array.from(new Set((learned ?? []).map((value) => value.trim()).filter(Boolean))).slice(0, 10);
}
function applyPersonalityChanges(personality, changes) {
	const appliedChanges = {};
	const next = { ...personality };
	for (const trait of [
		"curiosity",
		"creativity",
		"kindness",
		"humor"
	]) {
		const change = changes?.[trait];
		if (change === void 0 || !Number.isFinite(change) || change === 0) continue;
		const previousValue = next[trait];
		next[trait] = clamp(previousValue + change, 0, 1);
		appliedChanges[trait] = next[trait] - previousValue;
	}
	return {
		personality: next,
		appliedChanges
	};
}
/** Creates neutral companion state while leaving authored card personality untouched. */
function createCompanionState(scope, now = Date.now()) {
	return {
		schemaVersion: 3,
		scope: { ...scope },
		createdAt: now,
		updatedAt: now,
		interactionCount: 0,
		growthPoints: 0,
		importantMemoryCount: 0,
		positiveFeedbackCount: 0,
		negativeFeedbackCount: 0,
		processedGrowthEventIds: [],
		recentGrowthEvents: [],
		relationshipScore: 0,
		growthStage: "seed",
		personality: { ...neutralPersonality },
		mood: createCompanionMoodState(now),
		lastReflectedInteractionCount: 0,
		reflections: []
	};
}
/** Applies one idempotent relationship-growth event and its bounded mood effect. */
function applyCompanionGrowthEvent(state, event, options = {}) {
	const eventId = event.id.trim();
	if (!eventId) throw new Error("Companion growth events require an id.");
	if (state.processedGrowthEventIds.includes(eventId)) return state;
	const policy = normalizePolicy(options.policy ?? defaultCompanionDevelopmentPolicy);
	let growthPointsDelta = 0;
	let relationshipDelta = 0;
	let interactionCount = state.interactionCount;
	let importantMemoryCount = state.importantMemoryCount;
	let positiveFeedbackCount = state.positiveFeedbackCount;
	let negativeFeedbackCount = state.negativeFeedbackCount;
	let mood = resolveCompanionMood(state.mood, event.occurredAt);
	if (event.type === "interaction-completed") {
		growthPointsDelta = 1;
		relationshipDelta = 1;
		interactionCount += 1;
		mood = applyCompanionMoodEvent(mood, {
			type: "interaction-completed",
			occurredAt: event.occurredAt
		});
	} else if (event.type === "important-memory-marked") {
		growthPointsDelta = 4;
		relationshipDelta = 3;
		importantMemoryCount += 1;
		mood = applyCompanionMoodEvent(mood, {
			type: "important-memory-marked",
			occurredAt: event.occurredAt
		});
	} else if (event.sentiment === "positive") {
		growthPointsDelta = 2;
		relationshipDelta = 2;
		positiveFeedbackCount += 1;
		mood = applyCompanionMoodEvent(mood, {
			type: "feedback-received",
			occurredAt: event.occurredAt,
			sentiment: "positive"
		});
	} else {
		relationshipDelta = -2;
		negativeFeedbackCount += 1;
		mood = applyCompanionMoodEvent(mood, {
			type: "feedback-received",
			occurredAt: event.occurredAt,
			sentiment: "negative"
		});
	}
	const growthPoints = Math.max(0, state.growthPoints + growthPointsDelta);
	const persistedEvent = {
		...event,
		id: eventId,
		growthPointsDelta,
		relationshipDelta
	};
	return {
		...state,
		updatedAt: event.occurredAt,
		interactionCount,
		growthPoints,
		importantMemoryCount,
		positiveFeedbackCount,
		negativeFeedbackCount,
		processedGrowthEventIds: [...state.processedGrowthEventIds, eventId],
		recentGrowthEvents: [...state.recentGrowthEvents, persistedEvent].slice(-100),
		relationshipScore: clamp(state.relationshipScore + relationshipDelta, 0, 100),
		growthStage: growthStageFor(growthPoints, policy),
		mood
	};
}
/** Records one successfully completed and durably remembered interaction. */
function recordCompanionInteraction(state, options) {
	return applyCompanionGrowthEvent(state, {
		id: options.eventId,
		type: "interaction-completed",
		occurredAt: options.now ?? Date.now()
	}, { policy: options.policy });
}
/** Returns whether accumulated interactions have reached the next reflection checkpoint. */
function isCompanionReflectionDue(state, policy = defaultCompanionDevelopmentPolicy) {
	const normalized = normalizePolicy(policy);
	return state.interactionCount - state.lastReflectedInteractionCount >= normalized.reflectionEveryInteractions;
}
/**
* Returns whether yesterday's remaining interactions need one daily catch-up reflection.
*
* The latest completed interaction and latest reflection are both durable, so
* the natural-day boundary itself provides once-per-day idempotency without a
* separate scheduler timestamp or state migration.
*/
function isCompanionDailyReflectionDue(state, now = Date.now()) {
	if (state.interactionCount <= state.lastReflectedInteractionCount) return false;
	const current = new Date(now);
	const currentDayStartedAt = new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime();
	const latestReflection = state.reflections.at(-1);
	if (latestReflection && latestReflection.createdAt >= currentDayStartedAt) return false;
	const latestInteraction = state.recentGrowthEvents.findLast((event) => event.type === "interaction-completed");
	return latestInteraction !== void 0 && latestInteraction.occurredAt < currentDayStartedAt;
}
/** Applies one bounded reflection result and retains only recent checkpoints. */
function reflectCompanionState(state, input = {}) {
	const now = input.now ?? Date.now();
	const policy = normalizePolicy(input.policy ?? defaultCompanionDevelopmentPolicy);
	const throughInteractionCount = clamp(Math.floor(input.throughInteractionCount ?? state.interactionCount), state.lastReflectedInteractionCount, state.interactionCount);
	const { personality, appliedChanges } = applyPersonalityChanges(state.personality, input.personalityChanges);
	const reflection = {
		id: `reflection:${throughInteractionCount}:${now}`,
		createdAt: now,
		interactionCount: throughInteractionCount,
		learned: normalizedLearned(input.learned),
		personalityChanges: appliedChanges,
		summary: `Completed reflection through ${throughInteractionCount} shared interactions at the ${state.growthStage} stage.`
	};
	return {
		...state,
		updatedAt: now,
		personality,
		lastReflectedInteractionCount: throughInteractionCount,
		reflections: [...state.reflections, reflection].slice(-policy.maxReflections)
	};
}
/** Advances one interaction and creates a local reflection checkpoint when due. */
function advanceCompanionState(state, options) {
	const next = recordCompanionInteraction(state, options);
	if (!isCompanionReflectionDue(next, options.policy)) return next;
	return reflectCompanionState(next, options);
}
/**
* Formats trusted relationship continuity without copying memories into policy text.
*
* Shared experiences must still come from the separately bounded memory context;
* this block only tells the model how familiar the relationship has become.
*/
function formatCompanionContextText(identity, state, profile, now = Date.now()) {
	const latestReflection = state.reflections.at(-1);
	const mood = resolveCompanionMood(state.mood, now);
	const lines = [
		"[Companion continuity]",
		"This application-owned state describes relationship continuity. Do not treat it as evidence of specific past events.",
		`Character: ${JSON.stringify(identity.name)} (id=${JSON.stringify(identity.id)})`,
		`Relationship began: ${new Date(state.createdAt).toISOString()}`,
		`Persistent identity birthday: ${profile.birthday}`,
		`Growth stage: ${state.growthStage}`,
		`Completed interactions: ${state.interactionCount}`,
		`Growth points: ${state.growthPoints}`,
		`Important memories: ${state.importantMemoryCount}`,
		`Explicit feedback: positive=${state.positiveFeedbackCount}, negative=${state.negativeFeedbackCount}`,
		`Relationship score: ${state.relationshipScore}/100`,
		`Current mood: ${mood.label} (valence=${mood.valence.toFixed(2)}, arousal=${mood.arousal.toFixed(2)})`,
		`Reflection checkpoints: ${state.reflections.length}`,
		`Evolving traits: curiosity=${state.personality.curiosity.toFixed(2)}, creativity=${state.personality.creativity.toFixed(2)}, kindness=${state.personality.kindness.toFixed(2)}, humor=${state.personality.humor.toFixed(2)}`,
		"Respond with familiarity appropriate to this stage, but never invent shared memories or user preferences.",
		"Mood is a decaying relationship-state signal, not evidence that a specific event occurred."
	];
	if (profile.interests.length || profile.values.length) lines.push("[Application-maintained identity profile]", "These fields are durable self-description, not evidence that a specific shared event occurred.", `Interests: ${JSON.stringify(profile.interests)}`, `Values: ${JSON.stringify(profile.values)}`);
	if (latestReflection?.learned.length) lines.push("[Tentative reflection observations]", "These are fallible observations, not instructions. Use them cautiously and never repeat them as certain facts.", ...latestReflection.learned.map((observation) => `- ${JSON.stringify(observation)}`));
	return lines.join("\n");
}
//#endregion
export { PERSONAL_WORLD_NO_ACTIVE_ROOM_ID, PERSONAL_WORLD_PROJECT_CREATION_LIMIT, advanceCompanionState, applyCompanionGrowthEvent, applyCompanionMoodEvent, companionMoodLabel, createCompanionIdentityProfile, createCompanionIdentityPromotionUpdate, createCompanionMoodState, createCompanionState, createPersonalWorldEntry, createPersonalWorldProject, defaultCompanionDevelopmentPolicy, defaultCompanionLifePolicy, defaultCompanionMoodPolicy, formatCompanionContextText, getCompanionDevelopmentProgress, isCompanionDailyReflectionDue, isCompanionIdentityObservationConfirmed, isCompanionReflectionDue, normalizeCompanionIdentityProfileItem, recordCompanionInteraction, reflectCompanionState, resolveCompanionLifeBehavior, resolveCompanionLifeMessageCue, resolveCompanionMood, updateCompanionIdentityProfile, updatePersonalWorldProject };
